import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  Settings,
  Tag,
  X,
  Users,
  FileText,
  Calculator,
  Cog,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_OPTIONS = [
  { value: 'Users', label: 'Users', icon: Users },
  { value: 'FileText', label: 'FileText', icon: FileText },
  { value: 'Calculator', label: 'Calculator', icon: Calculator },
  { value: 'Settings', label: 'Settings', icon: Cog },
];

const iconMap = {
  Users: Users,
  FileText: FileText,
  Calculator: Calculator,
  Settings: Cog,
};

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal states
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    category: '',
    icon: 'FileText',
    tags: [],
    is_active: true,
  });
  
  // Tag input
  const [tagInput, setTagInput] = useState('');

  // Colors
  const colors = {
    dark: '#0D2440',
    blue: '#2E5E99',
    gray: '#5A7A9B',
    border: '#C8D8E8',
    bg: '#E7F0FA',
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/admin/services`),
        axios.get(`${API}/categories`)
      ]);
      setServices(servicesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/admin/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      category: '',
      icon: 'FileText',
      tags: [],
      is_active: true,
    });
    setTagInput('');
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({ 
      ...formData, 
      tags: formData.tags.filter(tag => tag !== tagToRemove) 
    });
  };

  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/admin/services`, formData);
      toast.success('Service erfolgreich erstellt');
      setIsCreateOpen(false);
      resetForm();
      fetchServices();
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Service');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const updateData = { ...formData };
      delete updateData.id; // Can't update ID
      
      await axios.put(`${API}/admin/services/${selectedService.id}`, updateData);
      toast.success('Service erfolgreich aktualisiert');
      setIsEditOpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren des Service');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsSubmitting(true);
    
    try {
      await axios.delete(`${API}/admin/services/${selectedService.id}`);
      toast.success('Service erfolgreich gelöscht');
      setIsDeleteOpen(false);
      setSelectedService(null);
      fetchServices();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen des Service');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (service) => {
    setSelectedService(service);
    setFormData({
      id: service.id,
      name: service.name,
      description: service.description,
      category: service.category,
      icon: service.icon || 'FileText',
      tags: service.tags || [],
      is_active: service.is_active !== false,
    });
    setTagInput('');
    setIsEditOpen(true);
  };

  const openDeleteModal = (service) => {
    setSelectedService(service);
    setIsDeleteOpen(true);
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.dark }}>Service-Verwaltung</h1>
            <p className="mt-1" style={{ color: colors.gray }}>
              Verwalten Sie Services und deren Tags
            </p>
          </div>
          <Button 
            onClick={() => { resetForm(); setIsCreateOpen(true); }} 
            data-testid="create-service-button"
            style={{ background: colors.dark }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Neuer Service
          </Button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: colors.gray }} />
          <Input
            placeholder="Suche nach Name, Kategorie oder Tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            style={{ borderColor: colors.border }}
            data-testid="service-search"
          />
        </div>

        {/* Services Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: colors.blue }} />
          </div>
        ) : services.length === 0 ? (
          <Card className="bg-white" style={{ borderColor: colors.border }}>
            <CardContent className="py-12 text-center">
              <Settings className="h-12 w-12 mx-auto mb-4" style={{ color: colors.gray }} />
              <h3 className="text-lg font-medium" style={{ color: colors.dark }}>Keine Services vorhanden</h3>
              <p className="mt-2" style={{ color: colors.gray }}>
                Erstellen Sie den ersten Service
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services
              .filter(service => {
                if (!searchTerm) return true;
                const search = searchTerm.toLowerCase();
                const nameMatch = service.name?.toLowerCase().includes(search);
                const idMatch = service.id?.toLowerCase().includes(search);
                const tagMatch = service.tags?.some(tag => tag.toLowerCase().includes(search));
                const categoryMatch = service.category?.toLowerCase().includes(search);
                const descMatch = service.description?.toLowerCase().includes(search);
                return nameMatch || idMatch || tagMatch || categoryMatch || descMatch;
              })
              .map((service) => {
              const IconComponent = iconMap[service.icon] || FileText;
              
              return (
                <Card 
                  key={service.id} 
                  className={`bg-white ${!service.is_active ? 'opacity-60' : ''}`}
                  style={{ borderColor: colors.border }}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-lg"
                          style={{ background: colors.bg }}
                        >
                          <IconComponent className="h-6 w-6" style={{ color: colors.blue }} />
                        </div>
                        <div>
                          <CardTitle style={{ color: colors.dark }}>{service.name}</CardTitle>
                          <p className="text-sm" style={{ color: colors.gray }}>{service.id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant={service.is_active ? 'default' : 'secondary'}
                          style={service.is_active ? { background: `${colors.blue}20`, color: colors.blue } : {}}
                        >
                          {service.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                      </div>
                    </div>
                    <CardDescription className="mt-2" style={{ color: colors.gray }}>
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" style={{ borderColor: colors.border, color: colors.gray }}>
                          {service.category}
                        </Badge>
                      </div>
                      
                      {/* Tags */}
                      {service.tags && service.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {service.tags.map((tag, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className="text-xs"
                              style={{ borderColor: colors.border, color: colors.gray }}
                            >
                              <Tag className="h-3 w-3 mr-1" />
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      
                      {/* Actions */}
                      <div className="flex items-center justify-end gap-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(service)}
                          data-testid={`edit-service-${service.id}`}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Bearbeiten
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteModal(service)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-service-${service.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Löschen
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Create Service Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ color: colors.dark }}>Neuen Service erstellen</DialogTitle>
              <DialogDescription style={{ color: colors.gray }}>
                Erstellen Sie einen neuen Service mit Tags.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-id">Service-ID</Label>
                    <Input
                      id="create-id"
                      value={formData.id}
                      onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      placeholder="z.B. mein_service"
                      required
                      data-testid="create-service-id"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="create-category">Kategorie</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="create-service-category">
                        <SelectValue placeholder="Kategorie wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ background: cat.color }}
                              />
                              {cat.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-name">Name</Label>
                  <Input
                    id="create-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Service-Name"
                    required
                    data-testid="create-service-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-description">Beschreibung</Label>
                  <Textarea
                    id="create-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Kurze Beschreibung des Service..."
                    required
                    data-testid="create-service-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-icon">Icon</Label>
                  <Select
                    value={formData.icon}
                    onValueChange={(value) => setFormData({ ...formData, icon: value })}
                  >
                    <SelectTrigger data-testid="create-service-icon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <opt.icon className="h-4 w-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagKeyPress}
                      placeholder="Tag eingeben und Enter drücken"
                      data-testid="create-service-tag-input"
                    />
                    <Button type="button" variant="outline" onClick={handleAddTag}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="gap-1">
                          {tag}
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => handleRemoveTag(tag)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="create-active">Aktiv</Label>
                  <Switch
                    id="create-active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="create-service-active"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isSubmitting} data-testid="submit-create-service" style={{ background: colors.dark }}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Erstellen
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Service Dialog */}
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ color: colors.dark }}>Service bearbeiten</DialogTitle>
              <DialogDescription style={{ color: colors.gray }}>
                Ändern Sie die Service-Informationen und Tags.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEdit}>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-id">Service-ID</Label>
                    <Input
                      id="edit-id"
                      value={formData.id}
                      disabled
                      className="opacity-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-category">Kategorie</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="edit-service-category">
                        <SelectValue placeholder="Kategorie wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ background: cat.color }}
                              />
                              {cat.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Service-Name"
                    required
                    data-testid="edit-service-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description">Beschreibung</Label>
                  <Textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Kurze Beschreibung des Service..."
                    required
                    data-testid="edit-service-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-icon">Icon</Label>
                  <Select
                    value={formData.icon}
                    onValueChange={(value) => setFormData({ ...formData, icon: value })}
                  >
                    <SelectTrigger data-testid="edit-service-icon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <div className="flex items-center gap-2">
                            <opt.icon className="h-4 w-4" />
                            {opt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagKeyPress}
                      placeholder="Tag eingeben und Enter drücken"
                      data-testid="edit-service-tag-input"
                    />
                    <Button type="button" variant="outline" onClick={handleAddTag}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {formData.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="gap-1">
                          {tag}
                          <X 
                            className="h-3 w-3 cursor-pointer" 
                            onClick={() => handleRemoveTag(tag)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="edit-active">Aktiv</Label>
                  <Switch
                    id="edit-active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="edit-service-active"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={isSubmitting} data-testid="submit-edit-service" style={{ background: colors.dark }}>
                  {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Service löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchten Sie den Service "{selectedService?.name}" wirklich löschen? 
                Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isSubmitting}
                data-testid="confirm-delete-service"
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Löschen
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default AdminServices;
